import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { readResponse, readResponseJSON } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_TARGETS = `R26-6543-2, C25-5796-7, C23-3910-3, R26-6582-1, R25-5918-2`;

const ImportProjects = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [targets, setTargets] = useState(DEFAULT_TARGETS);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [forceStatus, setForceStatus] = useState('');
  const [progress, setProgress] = useState({ phase: '', uploadPct: 0, elapsed: 0 });
  const xhrRef = useRef(null);
  const runKindRef = useRef(null); // 'dry-run' or 'import'

  if (!user?.is_admin) {
    return (
      <Layout>
        <div className="p-8 text-center text-red-600 font-medium" data-testid="admin-required">
          Admin access required.
        </div>
      </Layout>
    );
  }

  const runWithXHR = (endpoint) => new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('Please choose the .xlsx file first.'));
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    const token = localStorage.getItem('token');
    // Generate a job_id so the backend can attach progress to this run and the
    // frontend can poll. crypto.randomUUID() is supported in modern browsers.
    const jobId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const isActualImport = endpoint === '';
    const url = `${API_URL}/api/admin/import-projects${endpoint}?target_pids=${encodeURIComponent(targets.trim())}&force_status=${encodeURIComponent(forceStatus)}&job_id=${encodeURIComponent(jobId)}`;

    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;
    const started = Date.now();
    const elapsedTimer = setInterval(() => {
      setProgress(p => ({ ...p, elapsed: Math.round((Date.now() - started) / 1000) }));
    }, 250);

    // Poll the backend progress endpoint while the request is in flight (only for
    // the Actually-Import path — dry-run is fast and doesn't emit progress events).
    let pollTimer = null;
    if (isActualImport) {
      pollTimer = setInterval(async () => {
        try {
          const r = await fetch(`${API_URL}/api/admin/import-projects/progress/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!r.ok) return;
          const { data: snap } = await readResponse(r);
          if (snap && snap.phase) {
            setProgress(p => ({
              ...p,
              phase: snap.phase,
              uploadPct: snap.pct,
              elapsed: p.elapsed,
              total: snap.total,
              current: snap.current,
            }));
          }
        } catch { /* ignore polling errors */ }
      }, 500);
    }

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const pct = Math.round((e.loaded / e.total) * 100);
        setProgress(p => ({ ...p, phase: 'Uploading file…', uploadPct: pct }));
      }
    });
    xhr.upload.addEventListener('load', () => {
      setProgress(p => ({ ...p, phase: 'Server is parsing & importing…', uploadPct: 0 }));
    });
    xhr.addEventListener('load', () => {
      clearInterval(elapsedTimer);
      if (pollTimer) clearInterval(pollTimer);
      setProgress(p => ({ ...p, phase: 'Done', uploadPct: 100 }));
      let data = {};
      try { data = JSON.parse(xhr.responseText); } catch { /* keep empty */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new Error(data.detail || `HTTP ${xhr.status}`));
    });
    xhr.addEventListener('error', () => {
      clearInterval(elapsedTimer);
      if (pollTimer) clearInterval(pollTimer);
      reject(new Error('Network error'));
    });
    xhr.addEventListener('abort', () => {
      clearInterval(elapsedTimer);
      if (pollTimer) clearInterval(pollTimer);
      reject(new Error('Request aborted'));
    });

    setProgress({ phase: 'Uploading file…', uploadPct: 0, elapsed: 0 });
    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(fd);
  });

  const handleDryRun = async () => {
    setRunning(true);
    runKindRef.current = 'dry-run';
    setImportResult(null);
    setDryRunResult(null);
    try {
      const data = await runWithXHR('/dry-run');
      setDryRunResult(data);
      toast.success(`Dry-run: would import ${data.would_import_count}, skip ${data.would_skip_duplicate_count} duplicates`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRunning(false);
      xhrRef.current = null;
      runKindRef.current = null;
    }
  };

  const handleImport = async () => {
    if (!window.confirm(`Confirm: insert ${dryRunResult?.would_import_count ?? '?'} new projects into the live database? Existing Project #s will be skipped.`)) return;
    setRunning(true);
    runKindRef.current = 'import';
    try {
      const data = await runWithXHR('');
      setImportResult(data);
      toast.success(`Imported ${data.inserted_projects} projects, ${data.skipped_duplicates} skipped`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRunning(false);
      xhrRef.current = null;
      runKindRef.current = null;
    }
  };

  const handleStop = () => {
    if (!xhrRef.current) return;
    const kind = runKindRef.current;
    const msg = kind === 'import'
      ? 'Stop the import now? The browser will cancel the request, but if the server has already started writing batches, some projects may already exist in the database. You can re-run the import safely — duplicates are skipped automatically.'
      : 'Stop the dry-run now? It only reads, so nothing will be written either way.';
    if (!window.confirm(msg)) return;
    xhrRef.current.abort();
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8 max-w-5xl mx-auto">
        <div className="mb-6">
          <h2 className="text-lg text-[#5B8DB8] font-light">Admin</h2>
          <h1 className="text-3xl text-gray-700 font-light italic">Legacy Project Import</h1>
        </div>

        <button onClick={() => navigate('/setup')} className="mb-6 bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded transition-colors" data-testid="back-btn">Back</button>

        <div className="bg-white rounded shadow p-6 space-y-6">
          <div>
            <label className="block text-sm text-gray-700 mb-2 font-medium">1. Select the RestorePM_Projects .xlsx file</label>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => setFile(e.target.files[0] || null)}
              className="border border-gray-300 rounded px-3 py-2 w-full"
              data-testid="import-file-input"
            />
            {file && <p className="text-xs text-gray-500 mt-1">Selected: {file.name} ({Math.round(file.size / 1024)} KB)</p>}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm text-gray-700 font-medium">2. Target Project #s to import (comma- or whitespace-separated). Clear this box to import every row in the file.</label>
              <button
                type="button"
                onClick={() => setTargets('')}
                className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded transition-colors whitespace-nowrap ml-3"
                data-testid="clear-target-pids-btn"
                disabled={!targets}
              >
                Clear
              </button>
            </div>
            <textarea
              value={targets}
              onChange={(e) => setTargets(e.target.value)}
              rows={8}
              className="border border-gray-300 rounded px-3 py-2 w-full text-xs font-mono"
              data-testid="target-pids-input"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-2 font-medium">3. Force status (optional)</label>
            <select
              value={forceStatus}
              onChange={(e) => setForceStatus(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-full"
              data-testid="force-status-select"
            >
              <option value="">— Use the status from the spreadsheet —</option>
              <option value="open">All Available Projects (open)</option>
              <option value="ar">Accounts Receivable (ar)</option>
              <option value="closed">Closed</option>
              <option value="dead">Dead</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Overrides the spreadsheet's Status column so every imported project lands in the bucket you choose. Pick <strong>Accounts Receivable</strong> to send these into AR.</p>
          </div>

          <div className="flex gap-3 flex-wrap">
            <button
              onClick={handleDryRun}
              disabled={running}
              className="bg-[#4ECFDB] hover:bg-[#3AB8C5] disabled:opacity-50 text-white px-6 py-2 rounded transition-colors"
              data-testid="dry-run-btn"
            >
              {running ? 'Running…' : '4. Analyze / Dry Run'}
            </button>
            <button
              onClick={handleImport}
              disabled={running || !dryRunResult}
              className="bg-[#DC3545] hover:bg-[#C82333] disabled:opacity-50 text-white px-6 py-2 rounded transition-colors"
              data-testid="actual-import-btn"
              title={!dryRunResult ? 'Run the dry-run first' : ''}
            >
              {running ? 'Running…' : '5. Actually Import'}
            </button>
            {running && (
              <button
                onClick={handleStop}
                className="bg-gray-700 hover:bg-gray-800 text-white px-6 py-2 rounded transition-colors inline-flex items-center gap-2"
                data-testid="stop-import-btn"
              >
                <span className="w-2 h-2 rounded-sm bg-white"></span>
                Stop {runKindRef.current === 'import' ? 'Import' : 'Dry-Run'}
              </button>
            )}
          </div>

          {running && (
            <div className="border border-[#4ECFDB] bg-[#E7F8FB] rounded p-4 space-y-2" data-testid="progress-bar">
              <div className="flex items-center justify-between text-sm text-gray-700">
                <span className="font-medium">
                  {progress.phase || 'Starting…'}
                  {progress.uploadPct > 0 && ` — ${progress.uploadPct}%`}
                  {progress.total > 0 && progress.current >= 0 && progress.phase && !progress.phase.startsWith('Uploading') && (
                    <span className="text-gray-500 font-normal"> ({progress.current}/{progress.total})</span>
                  )}
                </span>
                <span className="text-xs text-gray-500" data-testid="progress-elapsed">{progress.elapsed}s elapsed</span>
              </div>
              <div className="w-full h-2 bg-white border border-[#4ECFDB]/40 rounded overflow-hidden relative">
                {progress.uploadPct > 0 ? (
                  <div
                    className="h-full bg-[#4ECFDB] transition-all"
                    style={{ width: `${progress.uploadPct}%` }}
                    data-testid="progress-fill-determinate"
                  />
                ) : (
                  <div
                    className="h-full w-1/3 bg-[#4ECFDB] absolute animate-import-pulse"
                    data-testid="progress-fill-indeterminate"
                  />
                )}
              </div>
              <p className="text-xs text-gray-500 italic">
                {progress.phase === 'Server is parsing & importing…'
                  ? 'Connecting to backend… progress will appear shortly.'
                  : progress.phase === 'Done'
                    ? 'Wrapping up…'
                    : progress.phase?.startsWith('Uploading')
                      ? 'Uploading the .xlsx to the server.'
                      : 'Don\'t refresh the page.'}
              </p>
            </div>
          )}

          {dryRunResult && (
            <div className="border-l-4 border-[#4ECFDB] bg-blue-50 p-4 rounded">
              <h3 className="font-medium text-gray-700 mb-2">Dry-Run Result</h3>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>Rows scanned: <strong>{dryRunResult.rows_scanned}</strong></li>
                <li>Would import: <strong>{dryRunResult.would_import_count}</strong></li>
                {dryRunResult.force_status_applied && (
                  <li>Force status applied to all imports: <strong>{dryRunResult.force_status_applied}</strong></li>
                )}
                <li>Would skip (already exist): <strong>{dryRunResult.would_skip_duplicate_count}</strong></li>
                {dryRunResult.missing_target_pids?.length > 0 && (
                  <li className="text-red-600">Target PIDs not found in file: {dryRunResult.missing_target_pids.join(', ')}</li>
                )}
                {dryRunResult.new_project_types_to_create?.length > 0 && (
                  <li>New Project Types to auto-create (0% commission): <strong>{dryRunResult.new_project_types_to_create.join(', ')}</strong></li>
                )}
                <li>New customers to auto-create: <strong>{dryRunResult.new_customers_to_create_count}</strong> (existing customers re-used: {dryRunResult.existing_customer_links})</li>
                {dryRunResult.new_customers_sample?.length > 0 && (
                  <li className="text-xs text-gray-600">
                    Sample new customers:
                    <ul className="list-disc list-inside ml-2 mt-1 space-y-1">
                      {dryRunResult.new_customers_sample.map((c, i) => (
                        <li key={i}>
                          <strong>{c.name}</strong> (#{c.customer_id})
                          {c.parsed_addresses?.map((a, j) => (
                            <span key={j} className="block ml-4 text-gray-500">→ {a.street_address}{a.unit ? ` · ${a.unit}` : ''}{a.city ? ` · ${a.city}` : ''}{a.state ? `, ${a.state}` : ''}{a.zip ? ` ${a.zip}` : ''}</span>
                          ))}
                        </li>
                      ))}
                    </ul>
                    {dryRunResult.new_customers_to_create_count > 10 && <span className="ml-2">…and {dryRunResult.new_customers_to_create_count - 10} more</span>}
                  </li>
                )}
                <li>Status breakdown: <code className="text-xs">{JSON.stringify(dryRunResult.status_breakdown)}</code></li>
                <li>Type breakdown: <code className="text-xs">{JSON.stringify(dryRunResult.type_breakdown)}</code></li>
              </ul>
            </div>
          )}

          {importResult && (
            <div className="border-l-4 border-green-500 bg-green-50 p-4 rounded" data-testid="import-result">
              <h3 className="font-medium text-gray-700 mb-2">Import Complete</h3>
              <ul className="text-sm text-gray-700 space-y-1">                <li>Inserted projects: <strong>{importResult.inserted_projects}</strong></li>
                <li>Skipped duplicates: <strong>{importResult.skipped_duplicates}</strong></li>
                <li>Auto-created Project Types: <strong>{importResult.inserted_project_types}</strong>{importResult.new_project_type_names?.length > 0 && ` (${importResult.new_project_type_names.join(', ')})`}</li>
                <li>Auto-created customers: <strong>{importResult.inserted_customers}</strong></li>
                {importResult.missing_target_pids?.length > 0 && (
                  <li className="text-red-600">Target PIDs not found in file: {importResult.missing_target_pids.join(', ')}</li>
                )}
                {importResult.row_error_count > 0 && (
                  <li className="text-red-700">
                    <strong>{importResult.row_error_count} row(s) had errors and were skipped:</strong>
                    <ul className="list-disc list-inside ml-2 mt-1 max-h-40 overflow-y-auto text-xs font-mono bg-red-50 border border-red-200 rounded p-2">
                      {importResult.row_errors.slice(0, 50).map((e, i) => (
                        <li key={i}><strong>{e.project_id}</strong>: {e.reason}</li>
                      ))}
                      {importResult.row_errors.length > 50 && <li>…and {importResult.row_errors.length - 50} more</li>}
                    </ul>
                    {(() => {
                      const errPids = (importResult.row_errors || [])
                        .map(e => e.project_id)
                        .filter(p => p && !p.startsWith('('));
                      if (errPids.length === 0) return null;
                      return (
                        <button
                          type="button"
                          onClick={() => {
                            setTargets(errPids.join(', '));
                            setImportResult(null);
                            setDryRunResult(null);
                            toast.info(`Pre-filled ${errPids.length} errored Project #${errPids.length === 1 ? '' : 's'} into the target list. Fix the source data (or settings) and run Analyze / Dry Run again.`);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="mt-3 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium px-3 py-1.5 rounded transition-colors"
                          data-testid="reimport-errored-btn"
                          title="Replace the target Project # list with only the rows that failed in this import, so you can retry just those"
                        >
                          Re-import only the errored rows ({errPids.length})
                        </button>
                      );
                    })()}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ImportProjects;
